import React from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {XMarkIcon} from '@heroicons/react/24/outline';
import {useUsersByClientAllQuery} from 'api';
import {useAssignUserTasksMutation} from 'api';
import {useForm, FormProvider} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';

interface EditModalProps {
  visible: boolean;
  onClose: () => void;
  selectedTaskIds: string[];
  onSuccess?: () => void;
}

interface FormData {
  userId: string;
}

const schema = Yup.object().shape({
  userId: Yup.string(),
});

const EditModal = ({
  visible,
  onClose,
  selectedTaskIds,
  onSuccess,
}: EditModalProps) => {
  const [assignUserTasks] = useAssignUserTasksMutation();

  const form = useForm<FormData>({
    defaultValues: {
      userId: '',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const {
    register,
    handleSubmit,
    formState: {isValid, isSubmitting, errors},
  } = form;

  const handleClose = () => {
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      await assignUserTasks({
        variables: {
          uuids: selectedTaskIds,
          userUuid: data.userId || null,
        },
      });
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const {data: {usersByClientAll: {users = []} = {}} = {}} =
    useUsersByClientAllQuery({
      variables: {
        search: {
          active: true,
        },
      },
      fetchPolicy: 'cache-first',
    });

  return (
    <Transition appear show={visible} as={React.Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => null}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-xl transform rounded bg-white text-left align-middle shadow-xl transition-all overflow-hidden">
                <FormProvider {...form}>
                  <form
                    className="w-full flex flex-col"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    {/* ヘッダー部分 */}
                    <div className="h-12 flex items-center px-6 gap-4 bg-white">
                      <h2 className="m-0 text-lg flex-1">一括編集</h2>
                      <button
                        type="button"
                        className="p-0 bg-transparent border-none cursor-pointer w-6 h-6"
                        onClick={handleClose}
                      >
                        <XMarkIcon className="h-6 w-6 text-gray-400" />
                      </button>
                    </div>

                    {/* コンテンツ部分 */}
                    <div className="p-6 bg-background rounded-b flex flex-col gap-2">
                      <div className="p-6 bg-white rounded flex items-start gap-4">
                        <div className="flex-1 flex flex-col gap-4">
                          <div className="flex flex-col gap-1">
                            <h4 className="m-0 font-bold">ToDo担当者</h4>
                            <select
                              {...register('userId')}
                              className={`form-select w-[300px] ${
                                errors.userId ? 'border-red-500' : ''
                              }`}
                            >
                              <option value=""></option>
                              {users?.map((user) => (
                                <option value={user.uuid} key={user.uuid}>
                                  {user?.lastName} {user.firstName}
                                </option>
                              ))}
                            </select>
                            {errors.userId && (
                              <span className="text-red-500 text-sm">
                                {errors.userId.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end px-6 py-3 gap-4 bg-background rounded-b">
                      <button
                        type="submit"
                        className="w-32 h-10 flex items-center justify-center rounded font-bold bg-primary text-white hover:bg-[var(--primary-button-hover-background)] disabled:bg-[var(--primary-button-disabled-background)] disabled:cursor-not-allowed"
                        disabled={!isValid || isSubmitting}
                      >
                        {isSubmitting ? '処理中...' : '一括変更'}
                      </button>
                    </div>
                  </form>
                </FormProvider>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditModal;
